import { getFeedbackLabelsToDisplay } from '@modules/feedback/components/FeedbackCard/utils';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { ReasonPrediction } from '@modules/feedback/store/types';
import { getReasonPredictions } from '@modules/feedback/utils/getReasonPredictions';
import { ManipulationType } from 'generated/graphql';
import { useMemo } from 'react';
import { FeedbackLabel, FeedbackLabelType } from 'v2/core/entities/feedback';
import { ReasonLabel } from 'v2/core/entities/feedback/labels';
import { UUID } from 'v2/core/entities/uuid';

const EMPTY_SUGGESTIONS: ReasonLabel[] = [];

interface useGetFeedbackReasonLabelsOptions {
  labels: ReasonLabel[];
  feedbackID: UUID;
  selectedLabels?: FeedbackLabel[];
  shouldSortBySelection?: boolean;
}

export const useGetFeedbackReasonLabels = ({
  labels,
  feedbackID,
  selectedLabels = [],
  shouldSortBySelection = false,
}: useGetFeedbackReasonLabelsOptions) => {
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );
  const editedRecords = useFeedbackOnFeedbackStore(
    ({ editedRecords }) => editedRecords,
  );

  const editedRecord = editedRecords.find(
    (editedRecord) => editedRecord.id === feedbackID,
  );

  const editedReasonPredictions = useMemo(
    () => getReasonPredictions(editedRecord),
    [editedRecord],
  );

  const feedbackReasonLabels = getFeedbackLabelsToDisplay({
    labels,
    selectedLabels,
    shouldSortBySelection,
  });

  const filteredRemovedReasonLabels = useMemo(
    () =>
      feedbackReasonLabels.filter((label): label is ReasonLabel => {
        if (label.type === FeedbackLabelType.Reason) {
          return !editedReasonPredictions?.find(
            (editedReasonPrediction) =>
              editedReasonPrediction.prediction.id === label.id,
          );
        }
        return false;
      }),
    [editedReasonPredictions, feedbackReasonLabels],
  );

  const alreadyAddedReasonLabels = useMemo(
    () =>
      editedReasonPredictions
        ?.filter(
          (editPrediction) =>
            editPrediction.manipulationType === ManipulationType.Add,
        )
        .map((reasonPrediction) => reasonPrediction.prediction) ??
      EMPTY_SUGGESTIONS,
    [editedReasonPredictions],
  );

  const currentlyAddingReasonLabels = useMemo(
    () =>
      recordInEditMode?.editPredictions
        ?.filter(
          (editPrediction): editPrediction is ReasonPrediction =>
            editPrediction.type === FeedbackLabelType.Reason &&
            editPrediction.manipulationType === ManipulationType.Add,
        )
        .map((reasonPrediction) => reasonPrediction.prediction) ??
      EMPTY_SUGGESTIONS,
    [recordInEditMode?.editPredictions],
  );

  const reasonLabels = useMemo(
    () =>
      Array.from(
        new Set([
          ...filteredRemovedReasonLabels,
          ...(editedRecord?.id === feedbackID
            ? alreadyAddedReasonLabels
            : EMPTY_SUGGESTIONS),
          ...(recordInEditMode?.id === feedbackID
            ? currentlyAddingReasonLabels
            : EMPTY_SUGGESTIONS),
        ]),
      ),
    [
      alreadyAddedReasonLabels,
      currentlyAddingReasonLabels,
      editedRecord?.id,
      feedbackID,
      filteredRemovedReasonLabels,
      recordInEditMode?.id,
    ],
  );

  const reasonLabelsGroupedByID: Record<string, ReasonLabel> = {};

  // In case of duplicacy within objects; only pick the recent occuring one
  // TODO(aryanshridhar): We don't need `recordsInEditMode` array in store anymore.
  // It wasn't removed while developing FOF, since previous version needed that.
  // Now that we are live, remove the `recordsInEditMode` array from store which
  // will simplify a lot of code
  for (const label of reasonLabels) {
    reasonLabelsGroupedByID[label.id] = label;
  }

  return Object.values(reasonLabelsGroupedByID);
};
